import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { helpPageStyles } from "../../core/styles/cards/helpPageStyles";
import { SUPPORT_CATEGORIES } from "../configs/helpConfig";
import { submitSupportRequest } from "../../../utils/supportService";

/**
 * Support request form. Collects a category, subject, and description and submits to the backend
 * intake endpoint. The signed-in user's identity/school is attached server-side.
 */
const SupportForm = () => {
  const theme = useTheme();
  const styles = helpPageStyles(theme);

  const [category, setCategory] = useState(SUPPORT_CATEGORIES[0]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  const canSubmit = subject.trim() && description.trim() && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await submitSupportRequest({
        kind: "support",
        category,
        subject: subject.trim(),
        message: description.trim(),
      });
      setSnack({ open: true, severity: "success", msg: "Thanks — your support request has been sent to our team." });
      setSubject("");
      setDescription("");
      setCategory(SUPPORT_CATEGORIES[0]);
    } catch (e) {
      setSnack({ open: true, severity: "error", msg: "Sorry, that didn't send. Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={styles.mdFormCard}>
      <Typography sx={styles.mdFormTitle}>Submit a support request</Typography>
      <Typography sx={styles.mdFormSubtitle}>
        Tell us what's going on and we'll follow up. Your name, email, and school are attached
        automatically.
      </Typography>

      <TextField
        select
        fullWidth
        size="small"
        label="Category"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        sx={styles.mdFormField}
      >
        {SUPPORT_CATEGORIES.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        size="small"
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        sx={styles.mdFormField}
      />

      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Describe your issue or question"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={styles.mdFormField}
      />

      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={handleSubmit}
        sx={styles.mdFormButton}
      >
        {submitting ? "Sending…" : "Send to support"}
      </Button>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportForm;
